/**
 * Jun-2026 (User-Wunsch) — kompakter „Master-Switch" für den Hauptsitz,
 * platziert RECHTS neben der jeweiligen Abschnitts-Überschrift.
 *
 * - Sichtbarer Ein-/Aus-Schalter (Übernahme & Überschreiben auf alle Filialen).
 * - Erklärtext steckt im ⓘ-Icon (Popover).
 * - Granulare Teilbereiche (Sub-Schalter) im Popover: einzelne Teilbereiche
 *   lassen sich von der Vererbung ausnehmen (Opt-out).
 * Rendert nichts für Filialen/standalone.
 */
import { useState, useEffect } from "react";
import { Info, Loader2, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

// Jun-2026 (User-Wunsch) — Vorschau „betroffene Filialen". Einmal pro Session
// die Filialen des Hauptsitzes laden (modulweiter Cache, vermeidet N Requests
// bei mehreren Schaltern auf der Seite).
let _hqBranchesCache = null;
let _hqBranchesPromise = null;
export function invalidateHqBranchesCache() { _hqBranchesCache = null; _hqBranchesPromise = null; }
function useHqBranches(enabled) {
    const [branches, setBranches] = useState(_hqBranchesCache || []);
    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        if (_hqBranchesCache) { setBranches(_hqBranchesCache); return; }
        if (!_hqBranchesPromise) {
            _hqBranchesPromise = api.get("/org/me")
                .then((r) => { _hqBranchesCache = r.data?.branches || []; return _hqBranchesCache; })
                .catch(() => { _hqBranchesPromise = null; return []; });
        }
        _hqBranchesPromise.then((b) => { if (alive) setBranches(b || []); });
        return () => { alive = false; };
    }, [enabled]);
    return branches;
}

function MiniSwitch({ on, busy, onClick, testId, size = "md", muted = false }) {
    const dims = size === "sm" ? "h-5 w-9" : "h-6 w-11";
    const knob = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
    const tx = size === "sm" ? (on ? "translate-x-[18px]" : "translate-x-1") : (on ? "translate-x-6" : "translate-x-1");
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-disabled={muted}
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className={`relative inline-flex ${dims} shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${muted ? "opacity-50" : ""} ${on ? "bg-[#FF8300]" : "bg-gray-300"}`}
            data-testid={testId}
        >
            {busy
                ? <Loader2 className={`${knob} animate-spin mx-auto text-white`} />
                : <span className={`inline-block ${knob} transform rounded-full bg-white transition-transform ${tx}`} />}
        </button>
    );
}

export default function BranchOverrideToggle({ area, currentUser, infoText, subGroups = [], testId }) {
    const { refresh } = useAuth();
    const isHauptsitz = currentUser?.branch_role === "hauptsitz";
    const [enabled, setEnabled] = useState(!!currentUser?.[`branch_override_${area}`]);
    const [excluded, setExcluded] = useState(currentUser?.[`branch_override_${area}_excluded`] || []);
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [fieldCount, setFieldCount] = useState(null);
    const branches = useHqBranches(isHauptsitz);

    if (!isHauptsitz) return null;

    const baseId = testId || `branch-override-${area}`;

    const push = async (nextEnabled, nextExcluded) => {
        setBusy(true);
        try {
            const r = await api.post(`/users/me/branch-override/${area}`, { enabled: nextEnabled, excluded: nextExcluded });
            setEnabled(nextEnabled);
            setExcluded(nextExcluded);
            await refresh();
            if (nextEnabled) toast.success(`Auf ${r.data?.updated ?? 0} Standort(e) übertragen & überschrieben.`);
            else toast.success("Übernahme für Standorte deaktiviert.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    // Aktivieren überschreibt Filialdaten → vorher Bestätigungs-Vorschau.
    const toggleMain = () => {
        if (!enabled) {
            if (branches.length === 0) { push(true, excluded); return; }
            // Feldanzahl für die Vorschau laden (best effort).
            api.get(`/users/me/branch-override/${area}`)
                .then((r) => setFieldCount(r.data?.field_count ?? null))
                .catch(() => setFieldCount(null));
            setConfirmOpen(true);
        } else {
            push(false, excluded);
        }
    };
    const confirmApply = () => { setConfirmOpen(false); push(true, excluded); };
    const toggleSub = (key) => {
        const next = excluded.includes(key) ? excluded.filter((k) => k !== key) : [...excluded, key];
        push(true, next);
    };

    return (
        <div className="flex items-center gap-1.5" data-testid={baseId} onClick={(e) => e.stopPropagation()}>
            <span className="hidden sm:inline text-[11px] font-bold text-[#111827] whitespace-nowrap">Org-weit{branches.length > 0 ? ` · ${branches.length}` : ""}</span>
            <Popover>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="shrink-0 text-gray-400 hover:text-[#111827]"
                        title="Org-weite Übernahme — Info & Teilbereiche"
                        data-testid={`${baseId}-info`}
                    >
                        <Info className="w-4 h-4" />
                    </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 text-sm leading-relaxed text-gray-700 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <p className="text-xs text-[#111827] font-bold">Für alle Standorte / Filialen übernehmen &amp; überschreiben</p>
                    <p className="text-xs text-gray-600">
                        Bei aktiviertem Schalter gelten die Einstellungen dieses Hauptsitzes für alle verknüpften
                        Standorte und überschreiben deren Einstellungen.
                    </p>
                    {infoText && <p className="text-xs text-gray-500">{infoText}</p>}
                    {/* Vorschau: betroffene Filialen (Jun-2026, User-Wunsch). */}
                    <div className="border-t border-gray-100 pt-2" data-testid={`${baseId}-affected`}>
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#111827]">
                            <Building2 className="w-3.5 h-3.5 text-[#2563EB]" />
                            {branches.length === 0
                                ? "Keine Filialen verknüpft"
                                : `Wirkt auf ${branches.length} ${branches.length === 1 ? "Filiale" : "Filialen"}`}
                        </div>
                        {branches.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5 max-h-32 overflow-y-auto" data-testid={`${baseId}-affected-list`}>
                                {branches.map((b) => (
                                    <li key={b.id} className="text-[11px] text-gray-600 flex items-center gap-1.5">
                                        <span className="w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                                        <span className="truncate">{b.company_name || b.name || "Filiale"}</span>
                                        {b.site_code && <span className="text-[10px] font-bold text-gray-400 shrink-0">· {b.site_code}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {subGroups.length > 0 && (
                        <div className="border-t border-gray-100 pt-2">
                            <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1.5">
                                Teilbereiche {enabled ? "(an = wird übernommen)" : "— erst nach Aktivierung"}
                            </div>
                            <div className="space-y-2">
                                {subGroups.map((sg) => {
                                    if (sg.fixedOff) {
                                        return (
                                            <div key={sg.key} className="flex items-center justify-between gap-2 text-xs opacity-70" data-testid={`${baseId}-sub-${sg.key}`}>
                                                <span className="text-gray-400">{sg.label}<br /><span className="text-[10px] text-gray-400">{sg.note || "pro Filiale — nicht übernehmbar"}</span></span>
                                                <MiniSwitch on={false} busy={true} onClick={() => {}} size="sm" testId={`${baseId}-sub-switch-${sg.key}`} />
                                            </div>
                                        );
                                    }
                                    const inherited = !excluded.includes(sg.key);
                                    return (
                                        <label key={sg.key} className="flex items-center justify-between gap-2 text-xs" data-testid={`${baseId}-sub-${sg.key}`}>
                                            <span className={enabled ? "text-[#111827]" : "text-gray-400"}>{sg.label}</span>
                                            <MiniSwitch
                                                on={enabled && inherited}
                                                busy={busy || !enabled}
                                                onClick={() => toggleSub(sg.key)}
                                                size="sm"
                                                testId={`${baseId}-sub-switch-${sg.key}`}
                                            />
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
            <MiniSwitch on={enabled} busy={busy} onClick={toggleMain} testId={`${baseId}-switch`} />

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent className="max-w-md" data-testid={`${baseId}-confirm-dialog`}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" /> Einstellungen org-weit übernehmen?
                        </DialogTitle>
                        <DialogDescription className="sr-only">Bestätigung der org-weiten Übernahme auf alle Filialen.</DialogDescription>
                    </DialogHeader>
                    <div className="text-sm text-gray-700 space-y-3">
                        <p data-testid={`${baseId}-confirm-summary`}>
                            {fieldCount != null && fieldCount > 0 && (
                                <><strong>{fieldCount} {fieldCount === 1 ? "Feld" : "Felder"}</strong> werden auf </>
                            )}
                            {(fieldCount == null || fieldCount === 0) && <>Diese Einstellungen werden auf </>}
                            <strong>{branches.length} {branches.length === 1 ? "Filiale" : "Filialen"}</strong> übertragen
                            und <strong>überschrieben &amp; gesperrt</strong>. Die Filialen können diese Felder danach nicht mehr selbst ändern.
                        </p>
                        {branches.length > 0 && (
                            <ul className="max-h-32 overflow-y-auto rounded-sm bg-gray-50 border border-gray-200 p-2 space-y-0.5" data-testid={`${baseId}-confirm-list`}>
                                {branches.map((b) => (
                                    <li key={b.id} className="text-xs text-gray-600 flex items-center gap-1.5">
                                        <span className="w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                                        <span className="truncate">{b.street || b.company_name || b.name || "Filiale"}</span>
                                        {b.site_code && <span className="text-[10px] font-bold text-gray-400 shrink-0">· {b.site_code}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm" data-testid={`${baseId}-confirm-cancel`}>Abbrechen</Button>
                        <Button onClick={confirmApply} disabled={busy} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid={`${baseId}-confirm-apply`}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Auf ${branches.length} ${branches.length === 1 ? "Filiale" : "Filialen"} übertragen`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Jun-2026 (User-Wunsch) — kompakter Org-Übernahme-Schalter DIREKT am Header
// eines Unterbereichs (ganz rechts), statt im Popover. Steuert die Aufnahme
// dieses Teilbereichs in die org-weite Übernahme (Opt-out via `excluded`).
export function BranchSubToggle({ area, subKey, currentUser, fixedOff = false, title }) {
    const { refresh } = useAuth();
    const isHauptsitz = currentUser?.branch_role === "hauptsitz";
    const mainEnabled = !!currentUser?.[`branch_override_${area}`];
    const [excluded, setExcluded] = useState(currentUser?.[`branch_override_${area}_excluded`] || []);
    const [busy, setBusy] = useState(false);

    if (!isHauptsitz) return null;

    const inherited = mainEnabled && !excluded.includes(subKey);
    const on = fixedOff ? false : inherited;

    const toggle = async () => {
        if (fixedOff) {
            toast.info("Dieser Teilbereich bleibt immer pro Filiale — nicht übernehmbar.");
            return;
        }
        if (!mainEnabled) {
            const labels = { marketplace: "Marktplatz", marketing: "Marketing" };
            toast.info(`Bitte zuerst die Org-Übernahme oben im ${labels[area] || "Bereichs"}-Kopf aktivieren.`);
            return;
        }
        const next = excluded.includes(subKey) ? excluded.filter((k) => k !== subKey) : [...excluded, subKey];
        setBusy(true);
        try {
            await api.post(`/users/me/branch-override/${area}`, { enabled: true, excluded: next });
            setExcluded(next);
            await refresh();
            toast.success(next.includes(subKey) ? "Aus Org-Übernahme ausgenommen" : "Wird org-weit übernommen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="flex items-center gap-1.5 shrink-0"
            onClick={(e) => { e.stopPropagation(); }}
            title={fixedOff ? "Pro Filiale — nicht übernehmbar" : "Org-weit übernehmen (an/aus)"}
            data-testid={`branch-sub-${area}-${subKey}`}
        >
            <span className="hidden md:inline text-[10px] font-bold uppercase tracking-wider text-gray-400">Org</span>
            <MiniSwitch on={on} busy={busy} muted={fixedOff || !mainEnabled} onClick={toggle} size="sm" testId={`branch-sub-switch-${area}-${subKey}`} />
        </div>
    );
}
